import { Box, IconButton, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { GoPlus } from 'react-icons/go';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import Search from 'components/Common/Search';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { useNavigate, useParams } from 'react-router-dom';
import RetrievalTestingButton from './RetrievalTestingButton';
import ExportIcon from '@/assets/icons/ExportIcon';
import ImportIcon from '@/assets/icons/ImportIcon';
import DownloadIcon from '@/assets/icons/DownloadIcon';
import { useDeleteChunk, useGetChunks, useGetKnowledgeBase } from '@/apis/business/setting/knowledgeBase/hook';
import { useMemo, useState } from 'react';
import http from '@/utils/http';
import { fileDownloader } from '@/utils/helperFunctions';
import { importQaChunk } from '@/apis/business/setting/knowledgeBase/api';
import { getSourceMeta, SOURCE_BADGE_SX } from '@/pages/Business/Agent/Configure/Knowledge/components/QuestionAnswer';
import { useGetCurrentUser } from '@/apis/user/hook';

export default function QuestionAnswer({ isLoading }: { isLoading?: boolean }) {
  const navigate = useNavigate();
  const { knowledgeBaseId } = useParams();
  const { currentPage: page, perPage, setPage, setPerPage } = usePagination();
  const { data: knowledgeBase } = useGetKnowledgeBase();
  const qaDataSetId = knowledgeBase?.default_qa_dataset_id?.id;
  const [searchText, setSearchText] = useState<null | string>(null);
  const theme = useTheme();
  const { data: currentUser } = useGetCurrentUser();
  const { data: QAChunksData, isLoading: loadingChunks } = useGetChunks(qaDataSetId, {
    'filter[content]': searchText,
    page,
    per_page: perPage
  });
  const { mutateAsync: deleteChunk } = useDeleteChunk(String(qaDataSetId));
  const onEdit = (id: any) => navigate(`${qaDataSetId}/qa-chunk/${id}`);
  const handleExport = async () => {
    const response = await http.getFile(
      `business/knowledgebases/${knowledgeBaseId}/datasets/${qaDataSetId}/chunks/export`
    );
    fileDownloader(response, 'agent-qa.csv');
  };
  const triggerFileInput = () => {
    const inputElement = document.getElementById('import-qa-chunk') as HTMLInputElement;
    inputElement?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      await importQaChunk(knowledgeBaseId!, String(qaDataSetId), formData);
    }
  };

  const memoizedQaChunks = useMemo(() => QAChunksData?.data ?? [], [QAChunksData?.data]);

  return (
    <>
      {isLoading || loadingChunks ? (
        <QAToolbarSkeleton />
      ) : (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap'>
          <Typography variant='h6'>Q&A</Typography>

          <Box display='flex' gap={1} flexWrap='wrap'>
            <Search
              size='small'
              placeholder='Search Question'
              searchText={searchText}
              setSearchText={setSearchText}
              sx={{ width: 260 }}
            />
            {['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(currentUser?.role ?? '') && (
              <Button
                sx={{
                  background: theme.palette.background.paper,
                  border: '1px solid #ECECEC',
                  minWidth: { xs: 'auto', lg: 88 },
                  px: { xs: 1.5, lg: 2 }
                }}
                color='secondary'
                variant='outlined'
                startIcon={<ExportIcon />}
                onClick={handleExport}
              >
                Export
              </Button>
            )}
            <label htmlFor='import-qa-chunk'>
              <input
                type='file'
                name='import-qa-chunk'
                id='import-qa-chunk'
                style={{ display: 'none' }}
                accept='.csv'
                onChange={handleFileChange}
              />
              <Button
                sx={{
                  background: theme.palette.background.paper,
                  border: '1px solid #ECECEC',
                  minWidth: { xs: 'auto', lg: 88 },
                  px: { xs: 1.5, lg: 2 }
                }}
                color='secondary'
                variant='outlined'
                startIcon={<ImportIcon />}
                onClick={triggerFileInput}
              >
                Import
              </Button>
            </label>
            <Tooltip title='Download sample file' arrow placement='top'>
              <IconButton
                href='/sampleQa.csv'
                download={true}
                sx={{
                  background: theme.palette.background.paper,
                  border: '1px solid #ECECEC',
                  p: { xs: 0.75, lg: 1 }
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <RetrievalTestingButton knowledgeBaseId={knowledgeBaseId} />
            <Button startIcon={<GoPlus />} onClick={() => navigate(`${qaDataSetId}/qa-chunk/add`)}>
              Add New
            </Button>
          </Box>
        </Box>
      )}

      <DataTable
        data={memoizedQaChunks}
        isLoading={loadingChunks || isLoading}
        isHeaderSticky
        columns={columns(onEdit, deleteChunk)}
      />

      <Pagination
        currentPage={page}
        totalPages={QAChunksData?.meta?.total_pages}
        totalCount={QAChunksData?.meta?.total_count ?? 0}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />
    </>
  );
}

const columns = (onEdit: (id: string) => void, onDelete: (id: string) => void): ColumnDef<any>[] => [
  {
    header: 'Question',
    accessorKey: 'question',
    cell: (info) => <ReactMarkdown rehypePlugins={[rehypeRaw]}>{info.getValue() as any}</ReactMarkdown>
  },
  {
    header: 'Answer',
    accessorKey: 'answer',
    cell: (info) => <ReactMarkdown rehypePlugins={[rehypeRaw]}>{info.getValue() as any}</ReactMarkdown>
  },
  {
    header: 'Source',
    cell: (info) => {
      const rowData = info.row.original as any;
      const { label, key } = getSourceMeta(rowData?.metadata?.source);
      return (
        <Box
          sx={{
            width: 'fit-content',
            px: 1.6,
            py: 0.35,
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.4,
            ...SOURCE_BADGE_SX[key]
          }}
        >
          {label}
        </Box>
      );
    }
  },
  {
    header: 'Action',
    cell: (info) => (
      <Box display='flex' gap={1}>
        <IconButton onClick={() => onEdit(info.row.original.id)}>
          <MdOutlineEdit />
        </IconButton>

        <IconButton color='error' onClick={() => onDelete(info.row.original.id)}>
          <RiDeleteBin6Line />
        </IconButton>
      </Box>
    )
  }
];

export const QAToolbarSkeleton = () => {
  return (
    <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap'>
      {/* Left: Title */}
      <Skeleton
        variant='text'
        width={60}
        height={28} // matches h6
        sx={{ mt: 0.5 }}
      />

      {/* Right: Actions */}
      <Box display='flex' gap={1} flexWrap='wrap'>
        {/* Search */}
        <Skeleton variant='rounded' width={260} height={40} />

        {/* Export */}
        <Skeleton variant='rounded' width={88} height={36} />

        {/* Import */}
        <Skeleton variant='rounded' width={88} height={36} />

        {/* Download Icon */}
        <Skeleton variant='circular' width={36} height={36} />

        {/* Retrieval Testing */}
        <Skeleton variant='rounded' width={140} height={36} />

        {/* Add New */}
        <Skeleton variant='rounded' width={120} height={36} />
      </Box>
    </Box>
  );
};
